import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useApp } from '../../contexts/AppContext';
import {
  type CatalogCollection,
  type CatalogEntry,
  getCatalogCollection,
  createCatalogCollection,
  listCatalogEntries,
  addCatalogEntry,
  updateCatalogEntry,
  deleteCatalogEntry,
  seedCatalogFromJson,
  generateTypeCode,
  deriveEntriesFromSeed,
} from '../TelemetryManagement/equipmentCatalog';
import '../TelemetryManagement/TelemetryManagementPage.css';
import './equipmentCatalog.css';

export default function EquipmentCatalogPage() {
  const { accessToken, createCtx } = useAuth();
  const { selectedProject, applicationNamespaces } = useApp();

  const [catalogCollection, setCatalogCollection] = useState<CatalogCollection | null>(null);
  const [entries, setEntries] = useState<CatalogEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [category, setCategory] = useState('');
  const [typeName, setTypeName] = useState('');
  const [typeCode, setTypeCode] = useState('');
  const [overrideCode, setOverrideCode] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const getCtx = useCallback(() => {
    // Use only the project's primary namespace for equipment catalog operations
    const ns0 = selectedProject?._namespaces?.[0];
    const namespaces = ns0 ? [ns0] : [];
    return createCtx(namespaces);
  }, [createCtx, selectedProject]);

  const refresh = useCallback(async () => {
    if (!accessToken || !selectedProject) return;
    setIsLoading(true);
    setError(null);
    try {
      const ctx = getCtx();
      const coll = await getCatalogCollection(ctx);
      setCatalogCollection(coll);
      if (coll) {
        const list = await listCatalogEntries(coll, ctx);
        setEntries(list);
      } else {
        setEntries([]);
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to load catalog');
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, selectedProject, getCtx]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    if (!overrideCode) setTypeCode(generateTypeCode(typeName));
  }, [typeName, overrideCode]);

  // Single action: Reset to Defaults (create/recreate and seed)
  const handleResetToDefaults = useCallback(async () => {
    if (!accessToken || !selectedProject) return;
    setIsLoading(true);
    setError(null);
    try {
      const ctx = getCtx();
      let coll = await getCatalogCollection(ctx);
      if (coll?._id) {
        await (await import('../../services/platformApi')).IafItemSvc.deleteNamedUserItem(coll._id, ctx, {});
      }
      const ns0 = selectedProject._namespaces?.[0];
      coll = await createCatalogCollection(ctx, ns0 ? [ns0] : [], 'Equipment Types Catalog');
      if (coll) {
        setCatalogCollection(coll);
        await seedCatalogFromJson(coll, ctx);
        const list = await listCatalogEntries(coll, ctx);
        setEntries(list);
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to reset to defaults');
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, selectedProject, getCtx]);

  // keep name but already defined above

  const submit = useCallback(async () => {
    if (!catalogCollection) {
      setError('Create the catalog first.');
      return;
    }
    if (!category || !typeName || !typeCode) {
      setError('Category, Type Name and Type Code are required');
      return;
    }
    const dup = entries.some(e => `${e.category.toLowerCase()}|${e.typeCode.toLowerCase()}` === `${category.toLowerCase()}|${typeCode.toLowerCase()}` && e._id !== editingId);
    if (dup) {
      setError('Duplicate Category + Type Code');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const ctx = getCtx();
      const entry = { category, typeName, typeCode } as CatalogEntry;
      if (editingId) {
        await updateCatalogEntry(catalogCollection, editingId, entry, ctx);
      } else {
        await addCatalogEntry(catalogCollection, entry, ctx);
      }
      const list = await listCatalogEntries(catalogCollection, ctx);
      setEntries(list);
      setIsModalOpen(false);
      setEditingId(null);
      setCategory(''); setTypeName(''); setTypeCode(''); setOverrideCode(false);
    } catch (e: any) {
      setError(e?.message || 'Failed to save entry');
    } finally {
      setIsLoading(false);
    }
  }, [catalogCollection, category, typeName, typeCode, editingId, entries, getCtx]);

  const onEdit = (e: CatalogEntry) => {
    setEditingId(e._id || null);
    setCategory(e.category);
    setTypeName(e.typeName);
    setTypeCode(e.typeCode);
    setOverrideCode(true);
    setIsModalOpen(true);
  };

  const onAdd = () => {
    setEditingId(null);
    setCategory('');
    setTypeName('');
    setTypeCode('');
    setOverrideCode(false);
    setIsModalOpen(true);
  };

  const onDelete = useCallback(async (e: CatalogEntry) => {
    if (!catalogCollection) return;
    if (!confirm(`Delete ${e.typeName} (${e.typeCode}) in ${e.category}?`)) return;
    setIsLoading(true);
    setError(null);
    try {
      const ctx = getCtx();
      if (e._id) await deleteCatalogEntry(catalogCollection, e._id, ctx);
      const list = await listCatalogEntries(catalogCollection, ctx);
      setEntries(list);
    } catch (err: any) {
      setError(err?.message || 'Failed to delete');
    } finally {
      setIsLoading(false);
    }
  }, [catalogCollection, getCtx]);

  // Group entries by category
  const groupedEntries = useMemo(() => {
    const sorted = [...entries].sort((a, b) => {
      const catCompare = a.category.localeCompare(b.category);
      if (catCompare !== 0) return catCompare;
      return a.typeName.localeCompare(b.typeName);
    });

    const groups: { [category: string]: CatalogEntry[] } = {};
    sorted.forEach(entry => {
      if (!groups[entry.category]) {
        groups[entry.category] = [];
      }
      groups[entry.category].push(entry);
    });

    return Object.entries(groups).sort(([catA], [catB]) => catA.localeCompare(catB));
  }, [entries]);

  // Default templates preview from JSON
  const defaultTemplates = useMemo(() => deriveEntriesFromSeed(), []);

  return (
    <div className="telemetry-management">
      <div className="telemetry-header">
        <div className="telemetry-title">
          <i className="fas fa-tags"></i>
          <h1>Equipment Catalog</h1>
        </div>
        <p className="telemetry-subtitle">Manage equipment categories and types for telemetry.</p>
      </div>

      {error && (
        <div className="telemetry-message error">
          <i className="fas fa-exclamation-circle"></i>
          <span>{error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      <div className="section-card">
        <div className="section-header">
          <h2>
            <i className="fas fa-database"></i>
            Catalog
          </h2>
          <div className="actions">
            <button className="btn-secondary" onClick={refresh} disabled={isLoading}>
              <i className="fas fa-sync-alt"></i>
              Refresh
            </button>
            <button className="btn-primary" onClick={onAdd} disabled={isLoading}>
              <i className="fas fa-plus"></i>
              Add Equipment Type
            </button>
            <button className="btn-primary" onClick={handleResetToDefaults} disabled={isLoading} title="Recreate collection and seed defaults">
              <i className="fas fa-undo"></i>
              Reset to Defaults
            </button>
          </div>
        </div>

        {isLoading && entries.length === 0 ? (
          <div className="loading-state"><i className="fas fa-spinner fa-spin"></i><span>Loading...</span></div>
        ) : entries.length === 0 ? (
          <div className="empty-state"><i className="fas fa-inbox"></i><p>No entries</p></div>
        ) : (
          <div className="equipment-catalog-list">
            {groupedEntries.map(([category, categoryEntries]) => (
              <div key={category} className="category-section">
                <div className="category-divider">
                  <h3>
                    <i className="fas fa-layer-group"></i>
                    {category}
                  </h3>
                  <span className="category-count">{categoryEntries.length} {categoryEntries.length === 1 ? 'type' : 'types'}</span>
                </div>
                <div className="collections-grid">
                  {categoryEntries.map((e) => (
                    <div key={`${e.category}::${e.typeCode}`} className="collection-card">
                      <div className="collection-header">
                        <h3>{e.typeName}</h3>
                        <div className="collection-actions">
                          <button className="btn-icon" onClick={() => onEdit(e)} title="Edit"><i className="fas fa-edit"></i></button>
                          <button className="btn-icon danger" onClick={() => onDelete(e)} title="Delete"><i className="fas fa-trash"></i></button>
                        </div>
                      </div>
                      <div className="collection-meta">
                        <span className="meta-item"><i className="fas fa-hashtag"></i>{e.typeCode}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>
                <i className={editingId ? "fas fa-edit" : "fas fa-plus"}></i>
                {editingId ? 'Edit Equipment Type' : 'Add Equipment Type'}
              </h2>
              <button className="btn-icon" onClick={() => { setIsModalOpen(false); setEditingId(null); setCategory(''); setTypeName(''); setTypeCode(''); setOverrideCode(false); }} title="Close">
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label>Category</label>
                  <input type="text" value={category} onChange={(e) => setCategory(e.target.value)} placeholder="e.g., HVAC" />
                </div>
                <div className="form-group">
                  <label>Type Name</label>
                  <input type="text" value={typeName} onChange={(e) => setTypeName(e.target.value)} placeholder="e.g., Air Handling Unit" />
                </div>
                <div className="form-group full-width">
                  <label>Type Code</label>
                  <div className="input-with-toggle">
                    <input type="text" value={typeCode} onChange={(e) => setTypeCode(e.target.value)} disabled={!overrideCode} placeholder="AUTO_GENERATED_FROM_NAME" />
                    <label className="toggle">
                      <input type="checkbox" checked={overrideCode} onChange={(e) => setOverrideCode(e.target.checked)} />
                      Manual override
                    </label>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => { setIsModalOpen(false); setEditingId(null); setCategory(''); setTypeName(''); setTypeCode(''); setOverrideCode(false); }}>Cancel</button>
              <button className="btn-primary" onClick={submit}>{editingId ? 'Save Changes' : 'Add Equipment Type'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Default Templates from JSON */}
      <div className="section-card">
        <h2>
          <i className="fas fa-book"></i>
          Default Templates ({defaultTemplates.length})
        </h2>
        <p className="section-description">
          These are the default equipment types parsed from <code>src/scripts/bim_equipment.json</code>. Use "Reset to Defaults" to recreate and seed the catalog.
        </p>
        <details className="templates-accordion">
          <summary>View Default Templates</summary>
          <div className="collections-grid small">
            {defaultTemplates.map((tpl) => (
              <div key={`${tpl.category}::${tpl.typeCode}`} className="collection-card compact">
                <div className="collection-header">
                  <h3>{tpl.typeName}</h3>
                </div>
                <div className="collection-meta">
                  <span className="meta-item"><i className="fas fa-layer-group"></i>{tpl.category}</span>
                  <span className="meta-item"><i className="fas fa-hashtag"></i>{tpl.typeCode}</span>
                </div>
              </div>
            ))}
          </div>
        </details>
      </div>
    </div>
  );
}
